import * as React from "react";
import Box from "@mui/material/Box";
import type { LtlFormCapabilities } from "@src/types/api";
import { BolDetailsAddressGrid, type BolDetailsAddressGridProps } from "./BolDetailsAddressGrid";
import { BolDetailsTrailingFields } from "./BolDetailsTrailingFields";
import { BolTimeInput } from "./BolTimeInput";
import type { BolDetailsFieldName, BolDetailsFormValues } from "./bolDetailsFormTypes";

export type { BolDetailsFieldName, BolDetailsFormValues } from "./bolDetailsFormTypes";

export interface BolDetailsFormProps {
  form: BolDetailsFormValues;
  errors: Record<string, string>;
  inferredFormFields?: Partial<Record<BolDetailsFieldName, boolean>>;
  caps: LtlFormCapabilities;
  onFieldChange: (name: BolDetailsFieldName, value: string) => void;
  onFieldBlur?: () => void;
  getTextFieldStyles: (fieldName: keyof BolDetailsFormValues) => unknown;
}

export function BolDetailsForm({
  form,
  errors,
  inferredFormFields = {},
  caps,
  onFieldChange,
  onFieldBlur,
  getTextFieldStyles,
}: BolDetailsFormProps) {
  const handleInputChange = (
    event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string
  ) => {
    const { name } = event.currentTarget;
    const el = event.currentTarget as HTMLInputElement | HTMLTextAreaElement;
    const resolved = newValue !== undefined && newValue !== null ? newValue : (el.value ?? "");

    onFieldChange(name as BolDetailsFieldName, resolved);
  };
  const gfs = getTextFieldStyles as BolDetailsAddressGridProps["getTextFieldStyles"];

  return (
    <Box className="bol-details-container" sx={styles.container}>
      <Box className="bol-details-grid" sx={styles.grid}>
        <BolDetailsAddressGrid
          form={form}
          errors={errors}
          caps={caps}
          onFieldBlur={onFieldBlur}
          handleInputChange={handleInputChange}
          getTextFieldStyles={gfs}
        />
        <BolTimeInput
          label="Pickup Open Time"
          name="pickupOpenTime"
          value={form.pickupOpenTime ?? ""}
          onChange={onFieldChange}
          onBlur={onFieldBlur}
          required={!!caps.requiresPickupOpenTime}
          errorMessage={errors.pickupOpenTime}
          inferred={!!inferredFormFields.pickupOpenTime}
        />
        <BolTimeInput
          label="Pickup Close Time"
          name="pickupCloseTime"
          value={form.pickupCloseTime ?? ""}
          onChange={onFieldChange}
          onBlur={onFieldBlur}
          required={!!caps.requiresPickupCloseTime}
          errorMessage={errors.pickupCloseTime}
          inferred={!!inferredFormFields.pickupCloseTime}
        />
        <BolDetailsTrailingFields
          form={form}
          errors={errors}
          caps={caps}
          onFieldChange={onFieldChange}
          handleInputChange={handleInputChange}
          onFieldBlur={onFieldBlur}
          gfs={gfs}
        />
      </Box>
    </Box>
  );
}

const styles = {
  container: {},
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    columnGap: 8,
    rowGap: 0,
    alignItems: "flex-start",
  },
};
